/**
 * 星球上的新鲜事（./worldFeed，feed v4 合同）的查询：30 秒轮询一次，页面不在前台时停（refetchIntervalInBackground: false）。
 * 读口照 ./usePublicMap 的写法：外面用 WorldFeedReaderProvider 换（测试、演示台）；没有就按数据模式选，live 用全站唯一的 HTTP 客户端。
 */
import { createContext, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/shared/api/client";
import { env } from "@/shared/config/env";
import { demoWorldFeedReader, FEED_POLL_MS, liveWorldFeedReader, type WorldFeed, type WorldFeedReader } from "./worldFeed";

const ReaderContext = createContext<WorldFeedReader | null>(null);

export function WorldFeedReaderProvider({ reader, children }: { reader: WorldFeedReader; children: ReactNode }) {
  return <ReaderContext.Provider value={reader}>{children}</ReaderContext.Provider>;
}

let live: WorldFeedReader | null = null;
let demo: WorldFeedReader | null = null;

export function useWorldFeedReader(): WorldFeedReader {
  const injected = useContext(ReaderContext);
  if (injected) return injected;
  if (env.dataMode === "fixture") return (demo ??= demoWorldFeedReader());
  return (live ??= liveWorldFeedReader(apiClient));
}

export const WORLD_FEED_KEY = ["pets", "public-world-feed"] as const;

/** 查询参数单独导出：用例钉轮询间隔与“后台不轮询”。 */
export const WORLD_FEED_QUERY = { staleTime: 15_000, refetchInterval: FEED_POLL_MS, refetchIntervalInBackground: false, retry: false } as const;

export function useWorldFeed() {
  const reader = useWorldFeedReader();
  return useQuery<WorldFeed>({ queryKey: WORLD_FEED_KEY, queryFn: ({ signal }) => reader.feed(signal), ...WORLD_FEED_QUERY });
}
